import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { Release, StreamSet, StreamTrack } from "../api/types";
import { bandcampEmbedFrom, embedURL, playError, registerAudio, takeAudio } from "./player";

/**
 * A card's player for a record Bandcamp does not stream: the shop's own clips,
 * in the slot the Bandcamp embed would have taken and at about its size.
 *
 * It is our own <audio> rather than an iframe, which is what makes it one
 * player for twelve shops: an MP3 from Hardwax's CDN and one from Clone's play
 * the same way, and the Speed test can pitch either. What differs per shop is
 * where the clip lives and how it is paired with a track, and that is the
 * server's business (internal/shopstream) -- the set arrives here already
 * mapped, with `source` saying which shop answered.
 *
 * A clip can also be an `embed`: a Bandcamp, YouTube or SoundCloud player a
 * Shopify shop pasted into its description. Those cannot be handled, only
 * shown, so they draw as an iframe when picked. A Bandcamp embed is redrawn
 * with the same embedURL a matched card uses, so it looks like one.
 */
export function ShopPlayer({ release, onNone, onClose }: {
  release: Release;
  /** The shops were asked and none plays a preview: the card can stop offering. */
  onNone: () => void;
  onClose: () => void;
}) {
  const [set, setSet] = useState<StreamSet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState({ at: 0, len: 0 });
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let alive = true;
    api.streams(release.key)
      .then((s) => {
        if (!alive) return;
        setSet(s);
        if (s.tracks.length === 0) onNone();
        // Start on the first thing that is audio: the point of the button
        // was to hear the record, and a Bandcamp embed needs a second click.
        const i = s.tracks.findIndex((x) => x.kind !== "embed");
        setPick(i >= 0 ? i : 0);
      })
      .catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [release.key, onNone]);

  // Starting the Speed test, or another card, should silence this.
  useEffect(() => {
    // The element the card's click primed, where there is one (see player.ts).
    const el = takeAudio();
    audio.current = el;
    const unregister = registerAudio(() => { el.pause(); });
    el.onplay = () => setPlaying(true);
    el.onpause = () => setPlaying(false);
    el.ontimeupdate = () => setT({ at: el.currentTime, len: el.duration || 0 });
    el.ondurationchange = () => setT((x) => ({ ...x, len: el.duration || 0 }));
    return () => { el.pause(); el.src = ""; unregister(); };
  }, []);

  const track: StreamTrack | undefined = set?.tracks[pick];

  // A new pick loads and plays. Autoplay is allowed because the play that
  // opened this player was a click; if a browser disagrees the list is still
  // there to press.
  useEffect(() => {
    const el = audio.current;
    if (!el || !track) return;
    if (track.kind === "embed") { el.pause(); return; }
    el.src = track.url;
    setT({ at: 0, len: 0 });
    setError(null);
    const advance = () => {
      const next = set!.tracks.findIndex((x, i) => i > pick && x.kind !== "embed");
      if (next >= 0) setPick(next); else setPlaying(false);
    };
    el.onended = advance;
    // A clip the shop no longer serves -- a 404 behind a button it still
    // draws -- is skipped rather than left as a silent player. The server
    // confirms clips before storing them now, but what was stored before it
    // did is still on file until the audio sweep retires it.
    el.onerror = advance;
    el.play().catch((e: unknown) => { setPlaying(false); setError(playError(e)); });
  }, [track?.url, track?.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(i: number) {
    const el = audio.current;
    if (!el) return;
    if (i !== pick) { setPick(i); return; }
    if (set?.tracks[i]?.kind === "embed") return;
    if (el.paused) void el.play().catch((e: unknown) => setError(playError(e))); else el.pause();
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const el = audio.current;
    if (!el || !t.len) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - r.left) / r.width) * t.len;
  }

  const src = set?.source;
  return (
    <div className="shp">
      <div className="hd">
        <span className="from">
          {src ? <>Preview from {src.url
                   ? <a href={src.url} target="_blank" rel="noopener noreferrer">{src.label} ↗</a>
                   : src.label}</>
               : set ? "No shop preview" : "asking the shops…"}
        </span>
        {track && track.kind !== "embed" && (
          <span className="time">{fmt(t.at)}{t.len ? ` / ${fmt(t.len)}` : ""}</span>
        )}
        <button className="x" onClick={onClose} aria-label="close the player" title="close">×</button>
      </div>

      {error && <div className="note">{error}</div>}
      {set && set.tracks.length === 0 && (
        <div className="note">None of the shops carrying this record plays a preview.</div>
      )}

      {track && track.kind !== "embed" && (
        <div className="bar" onClick={seek} role="progressbar"
             aria-valuenow={t.len ? Math.round((t.at / t.len) * 100) : 0}>
          <div className="fill" style={{ width: t.len ? `${(t.at / t.len) * 100}%` : 0 }} />
        </div>
      )}

      {track && track.kind === "embed" && <EmbedFrame url={track.url} title={release.title} />}

      {set && set.tracks.length > 0 && (
        <ul>
          {set.tracks.map((x, i) => {
            const on = i === pick;
            return (
              <li key={x.pos} className={on ? "on" : ""}>
                <button className={`p${on && playing ? " on" : ""}`} onClick={() => toggle(i)}
                        title={x.kind === "embed" ? "show this player" : on && playing ? "pause" : "play"}>
                  {x.kind === "embed" ? "▣" : on && playing ? "❚❚" : "▶"}
                </button>
                <span className="lb">{x.label || x.pos}</span>
                <span className="tt" title={x.title}>{x.title}</span>
                {x.bpm && <span className="d">{x.bpm} BPM</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Somebody else's player, at the height each one wants. */
function EmbedFrame({ url, title }: { url: string; title: string }) {
  const bc = bandcampEmbedFrom(url);
  if (bc) {
    return <iframe loading="lazy" src={embedURL(bc.id, bc.itemType)} title={`${title} on Bandcamp`} />;
  }
  const yt = url.includes("youtube.com/embed/") || url.includes("youtube-nocookie.com/embed/");
  return (
    <iframe loading="lazy" className={yt ? "yt" : "sc"} src={url} title={title}
            allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
  );
}

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}
